/**
 * T-141-002 — Brevo Form Email Capture for LinkedIn CTA Submissions
 *
 * Feature: F-184 / US-141
 * Captures email leads from LinkedIn post calls-to-action and submits to the
 * existing Brevo contact sync endpoint (`POST api/marketing/contact`).
 *
 * Requirements:
 * - Direct submission to existing BE endpoint (no new routes)
 * - Clear validation, loading, success, and error states
 * - Segmentation by plan interest (Independent, Essentials, Professional, Enterprise)
 * - Optional portfolio size & phone capture
 */

import React, { useState } from 'react';
import { landlordApi } from '../../services/api';
import { BrevoLeadCapturePayload, LinkedInPlanType } from './types';

export interface LinkedInBrevoCtaFormProps {
  defaultPlanType?: LinkedInPlanType | string;
  onSuccess?: (payload: BrevoLeadCapturePayload) => void;
  className?: string;
  title?: string;
  subtitle?: string;
}

export const LinkedInBrevoCtaForm: React.FC<LinkedInBrevoCtaFormProps> = ({
  defaultPlanType = 'essentials',
  onSuccess,
  className = '',
  title = 'Get Your Free Texas Landlord Compliance Pack',
  subtitle = 'Enter your details below to receive our statutory checklists, notice templates, and consultation access directly to your inbox.',
}) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [planType, setPlanType] = useState<string>(defaultPlanType);
  const [units, setUnits] = useState<string>('1-4');
  const [notes, setNotes] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError('Please provide a valid email address.');
      return;
    }

    // Basic email format check
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError('Please enter a valid email format (e.g. landlord@example.com).');
      return;
    }

    const nameParts = (name.trim() || 'Landlord Lead').split(' ');
    const firstName = nameParts[0] || 'Landlord';
    const lastName = nameParts.slice(1).join(' ') || 'Partner';

    const numUnits = parseInt(units.split('-')[0].replace(/\D/g, ''), 10) || 1;

    setLoading(true);

    try {
      // Existing Brevo capture contract: POST api/marketing/contact
      await landlordApi.post('api/marketing/contact', {
        email: trimmedEmail,
        firstName,
        lastName,
        phone: phone.trim() || undefined,
        units: numUnits,
        marketingConsent: true,
        plan: planType,
        listIds: [3],
        attributes: {
          PRODUCT: 'LinkedIn_Growth_CTA',
          FIRSTNAME: firstName,
          LASTNAME: lastName,
          SMS: phone.trim() || undefined,
          UNITS: numUnits,
          PLAN_INTEREST: planType,
          NOTES: notes.trim() || undefined,
          SOURCE: 'linkedin_template_cta',
        },
      });

      setSuccess(true);
      if (onSuccess) {
        onSuccess({
          email: trimmedEmail,
          name: name.trim(),
          planType,
          units: numUnits,
          phone: phone.trim(),
          notes: notes.trim(),
        });
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Unable to submit at this time. Please try again or book directly via the calendar.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="linkedin-brevo-form"
      className={`bg-white border border-slate-200 rounded-xl p-6 shadow-sm ${className}`}
    >
      <div className="mb-5">
        <h3 className="text-xl font-bold text-slate-900">{title}</h3>
        {subtitle && <p className="text-sm text-slate-600 mt-1">{subtitle}</p>}
      </div>

      {success ? (
        <div
          data-testid="cta-success-message"
          className="p-5 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-900 space-y-2"
        >
          <div className="flex items-center gap-2 font-bold text-emerald-800">
            <i className="fas fa-check-circle text-lg text-emerald-600"></i>
            <span>Compliance Pack Dispatched!</span>
          </div>
          <p className="text-sm text-emerald-700">
            We’ve sent your Texas landlord statutory starter pack and onboarding checklist to{' '}
            <strong>{email}</strong>. Check your inbox (and spam folder) within the next few minutes.
          </p>
          <button
            type="button"
            onClick={() => {
              setSuccess(false);
              setEmail('');
              setName('');
              setNotes('');
            }}
            className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 underline mt-2"
          >
            Submit another inquiry
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div
              data-testid="cta-error-message"
              className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-rose-800 text-sm flex items-start gap-2"
            >
              <i className="fas fa-exclamation-triangle text-rose-500 mt-0.5"></i>
              <span>{error}</span>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
              Email Address <span className="text-rose-500">*</span>
            </label>
            <input
              data-testid="cta-email-input"
              type="email"
              required
              placeholder="landlord@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                Your Name
              </label>
              <input
                data-testid="cta-name-input"
                type="text"
                placeholder="Jane Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                Phone (Optional)
              </label>
              <input
                data-testid="cta-phone-input"
                type="tel"
                placeholder="(512) 555-0199"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                Plan of Interest
              </label>
              <select
                data-testid="cta-plan-select"
                value={planType}
                onChange={(e) => setPlanType(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="independent">Independent Landlord (1–4 units)</option>
                <option value="essentials">Essentials (Automation)</option>
                <option value="professional">Professional (5–20 units)</option>
                <option value="enterprise">Enterprise (20+ units / Agency)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                Managed Units
              </label>
              <select
                data-testid="cta-units-select"
                value={units}
                onChange={(e) => setUnits(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="1">1 Unit</option>
                <option value="2-4">2–4 Units</option>
                <option value="5-20">5–20 Units</option>
                <option value="21+">21+ Units</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
              Primary Compliance Challenge (Optional)
            </label>
            <textarea
              data-testid="cta-notes-input"
              rows={2}
              placeholder="e.g. City of Dallas registration, tenant notices, HVAC compliance..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <button
            data-testid="cta-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white font-bold text-sm rounded-lg shadow-sm transition flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                <span>Submitting to Brevo...</span>
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane"></i>
                <span>Claim Compliance Pack & Consultation</span>
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
};

export default LinkedInBrevoCtaForm;
